import { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { MemberMe } from 'src/modules/member/models/member';
import { User } from '../models/user';
import { LocalStorageKeys } from '../models/enums/localStorageKeys.enum';
import { SystemMessage } from '../models/gui/systemMessage';
import { devCheck } from '../functions/devCheck';
import { clearAllExportLocksLocalStorage, EXPORT_BUTTON_LOCKS, loadLockedButtons } from '../functions/exportLockHandler';
import { ExportButtonEnum } from '../models/enums/exportButton.enum';

export const drawerWidth = 250;

export interface Globals {
  systemMessage: SystemMessage | undefined;
  setSystemMessage: (message: SystemMessage | undefined) => void;
  currentLanguage: string;
  setCurrentLanguage: (lang: string) => void;
  currentUser: User | undefined;
  setCurrentUser: (user: User) => void;
  sideMenuOpen: boolean;
  setSideMenuOpen: (open: boolean) => void;
  currentMember: MemberMe | undefined;
  setCurrentMember: (member: MemberMe) => void;
  dateStamp: Date | undefined;
  setDateStamp: (date: Date) => void;
  cleanLocalStorage: () => void;
  exportLocks: ExportButtonEnum[];
  setExportLocks: Dispatch<SetStateAction<ExportButtonEnum[]>>;
}

// TODO: Language options och set language möjligen. det finns dock redan context för det i i18n. så kanske skippa?

export const GlobalContext = createContext<Globals>({
  systemMessage: undefined,
  setSystemMessage: () => undefined,
  currentLanguage: 'sv',
  setCurrentLanguage: () => undefined,
  currentUser: undefined,
  setCurrentUser: () => undefined,
  sideMenuOpen: true,
  setSideMenuOpen: () => undefined,
  currentMember: undefined,
  setCurrentMember: () => undefined,
  dateStamp: undefined,
  setDateStamp: () => undefined,
  cleanLocalStorage: () => undefined,
  exportLocks: [],
  setExportLocks: (() => undefined) as Dispatch<SetStateAction<ExportButtonEnum[]>>,
});
export const useGlobals = () => {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error('useGlobals must be within AppContext');
  }
  return context;
};
interface Props {
  children: JSX.Element;
}

export const AppContext = ({ children }: Props) => {
  const [systemMessage, setSystemMessage] = useState<SystemMessage | undefined>();
  const [sideMenuOpen, setSideMenuOpen] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<User | undefined>();
  const [currentMember, setCurrentMember] = useState<MemberMe | undefined>();
  const [currentLanguage, setCurrentLanguage] = useState<string>('sv');
  const [dateStamp, setDateStamp] = useState<Date | undefined>(undefined);
  const [exportLocks, setExportLocks] = useState<ExportButtonEnum[]>(() => loadLockedButtons());

  useEffect(() => {
    devCheck();
  }, []);

  // Synkar export-lås mellan flikar via "storage"-event; uppdaterar Context när våra LS-keys ändras (triggeras bara i andra flikar).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key && EXPORT_BUTTON_LOCKS.some((c) => c.localStorageKey === e.key)) {
        setExportLocks(loadLockedButtons());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const cleanLocalStorage = () => {
    localStorage.removeItem(LocalStorageKeys.MemberOverviewFilter);
    localStorage.removeItem(LocalStorageKeys.MemberAdvancedSearchFilter);
    localStorage.removeItem(LocalStorageKeys.InvoiceSearchFilter);
    localStorage.removeItem(LocalStorageKeys.InvoiceMissionDetailFilter);
    localStorage.removeItem(LocalStorageKeys.GroupMessageEmailSearchFilter);
    localStorage.removeItem(LocalStorageKeys.GroupMessageSmsSearchFilter);
    localStorage.removeItem(LocalStorageKeys.IncomingBankPaymentSearchFilter);
    localStorage.removeItem(LocalStorageKeys.PaymentAccountingSearchFilter);
    localStorage.removeItem(LocalStorageKeys.PaymentTransactionSearchFilter);
    localStorage.removeItem(LocalStorageKeys.GiftSearchFilter);
    localStorage.removeItem(LocalStorageKeys.ExportFileSearchFilter);
    localStorage.removeItem(LocalStorageKeys.FundraiserSettingsSearchFilter);
    localStorage.removeItem(LocalStorageKeys.EventSearchFilter);
    localStorage.removeItem(LocalStorageKeys.EventDateFilterCalendar);
    localStorage.removeItem(LocalStorageKeys.EventDateFilterList);
    localStorage.removeItem(LocalStorageKeys.EventDateMemberMeFilterCalendar);
    localStorage.removeItem(LocalStorageKeys.InvoiceAddressSearchFilter);
    localStorage.removeItem(LocalStorageKeys.AutogiroConsentSearchFilter);
    localStorage.removeItem(LocalStorageKeys.AutogiroCreateFileSearchFilter);
    localStorage.removeItem(LocalStorageKeys.StatisticsMemberGrowthSearchFilter);
    clearAllExportLocksLocalStorage();
    setExportLocks([]);
  };

  const globalContext = useMemo(
    () => ({
      systemMessage,
      setSystemMessage,
      currentLanguage,
      setCurrentLanguage,
      currentUser,
      setCurrentUser,
      sideMenuOpen,
      setSideMenuOpen,
      currentMember,
      setCurrentMember,
      dateStamp,
      setDateStamp,
      cleanLocalStorage,
      exportLocks,
      setExportLocks,
    }),
    [systemMessage, currentLanguage, currentUser, sideMenuOpen, currentMember, dateStamp, exportLocks],
  );

  return <GlobalContext.Provider value={globalContext}>{children}</GlobalContext.Provider>;
};
